/* B2B catalogue visibility — the single source of truth for the Algolia filter.
 *
 * Model: absence means visible.
 *
 *   Ordinary product          no restriction marker  -> visible to everyone
 *   Company-exclusive product marker + allowed ids   -> visible only to those
 *
 * The marker is the product tag "b2b-only". The "restricted" value inside
 * custom.b2b_location_ids is still honoured, but it cannot be relied on alone:
 * an app keeps that metafield in step with the B2B catalogs and rewrites it as
 * a bare list of location ids, dropping the marker (seen 2026-09-01,
 * 2026-10-19 and 2026-11-01). No writer of tags has been seen, but tags are
 * not guaranteed either: a product import that replaces the tag set would
 * drop it, and a product taken off the D2C catalogs is not tagged
 * automatically. The location ids that app writes are still what lets a buyer
 * see the exclusive.
 *
 * Shopify B2B catalogs remain the source of truth for access and pricing. This
 * only decides what appears in Algolia-backed search, which catalogs cannot
 * reach.
 *
 * Implemented with Algolia's own semantics rather than a synthesised
 * "visible to all" token: `NOT attribute:value` already matches records where
 * the attribute is absent. Verified against the live index —
 * `NOT meta.custom.b2b_location_ids:"4853072045"` returns exactly the 6 records
 * that carry no metafield, exhaustiveNbHits true. So a marker value inside the
 * existing, already-indexed attribute gives the intended behaviour with no
 * indexing transform and no second metafield.
 *
 * Filters produced:
 *
 *   guest / D2C   NOT tags:"b2b-only"
 *                 AND NOT meta.custom.b2b_location_ids:"restricted"
 *   B2B buyer     (NOT tags:"b2b-only"
 *                  OR meta.custom.b2b_location_ids:"<location id>")
 *                 AND (NOT meta.custom.b2b_location_ids:"restricted"
 *                  OR meta.custom.b2b_location_ids:"<location id>")
 *   restricted    <B2B buyer> AND (id=<product> OR id=<product> ...)
 *   B2B buyer     — only when flagged to their company's My Items; see
 *                   "Item restriction" below
 *
 * Guests never see exclusives, which the old allowlist design could not do.
 *
 * Ordering is not a hazard: until a product actually carries the marker, the
 * filter matches everything, so this can ship before any product is marked and
 * before the stale uniform lists are cleaned up. Those legacy 13-id lists do
 * not contain the marker, so they read as unrestricted — cleanup is optional,
 * not a prerequisite.
 *
 * Replaces the previous allowlist filter, where a company location absent from
 * every product matched zero products and emptied the storefront (Shook
 * Construction, location 5209096365). Under this model a location that is
 * simply unknown still sees the unrestricted catalogue, so that failure mode is
 * gone by construction and the fail-open probe it needed is gone with it.
 */
(function () {
  'use strict';

  var ATTR = 'meta.custom.b2b_location_ids';
  /* Sentinel stored alongside the allowed location ids on a restricted product.
   * Kept in this attribute rather than a new one so nothing has to change in
   * the Algolia index configuration. */
  var MARKER = 'restricted';
  /* The marker that survives the metafield being rewritten; see the header. */
  var TAG = 'b2b-only';
  var MARKERS = ['tags:"' + TAG + '"', ATTR + ':"' + MARKER + '"'];

  var UNRESTRICTED = MARKERS.map(function (m) { return 'NOT ' + m; }).join(' AND ');

  function locationId() {
    var b2b = window.algoliaB2B;
    if (!b2b) return null;
    /* rawLocationId is what earlier builds nulled out; prefer the live value
     * and fall back so a cached page cannot lose the buyer's location. */
    return b2b.locationId || b2b.rawLocationId || null;
  }

  /* ── Item restriction ────────────────────────────────────────────────
   *
   * A buyer flagged jendco.restrict_to_my_items may only order their company's
   * My Items. layout/theme.liquid exposes that list as allowedProductIds, or
   * null for everyone else — including a flagged buyer whose list is empty,
   * because snippets/b2b-allowed-product-ids.liquid fails open. The cart
   * refuses checkout for anything else; narrowing search here is so the buyer
   * is not shown what they cannot order. */
  var allowedList = (function () {
    var ids = window.algoliaB2B && window.algoliaB2B.allowedProductIds;
    if (!Array.isArray(ids) || !ids.length) return null;
    var seen = {};
    return ids.map(String).filter(function (id) {
      if (seen[id]) return false;
      seen[id] = true;
      return true;
    });
  })();
  var allowedIds = null;
  if (allowedList) {
    allowedIds = {};
    allowedList.forEach(function (id) { allowedIds[id] = true; });
  }

  /* The storefront proxy (Cloudflare Worker jendco-algolia-proxy) rejects a
   * request whose `filters` exceed its limit, and a rejected request is a
   * search that shows nothing. The Worker allows 4,000 characters (raised from
   * 750 for this) and 8,000 of encoded params; 3,500 leaves room for the
   * clause a caller ANDs on — collection pages add `collections:<handle>` —
   * and holds about 169 product ids. Keep in step with the Worker, the same
   * way its facet allowlist is kept in step with the theme's. */
  var MAX_FILTER_LENGTH = 3500;
  var warnedTooLong = false;

  /* The filter every Algolia call site should apply. Returns a string that is
   * always safe to AND into a wider filter expression: each OR group is
   * parenthesised, and the groups are joined by a bare AND. Never wrap an AND in
   * parentheses — Algolia rejects it ("filter (X AND Y) OR Z is not allowed,
   * only (X OR Y) AND Z is allowed"), so callers may AND this but never OR it.
   *
   * For a restricted buyer it also limits results to their My Items. A list
   * too long for the proxy falls back to the location scope alone — the full
   * catalogue in search, with the cart still enforcing — rather than a search
   * that fails. Pass { ignoreItemRestriction: true } for a SKU lookup that must
   * find the product in order to explain why it cannot be ordered. */
  function jendcoB2BFilter(options) {
    var loc = locationId();
    /* One OR group per marker: Algolia cannot OR an AND, so the buyer's location
     * goes into each group rather than around UNRESTRICTED. */
    var scope = loc
      ? MARKERS.map(function (m) { return '(NOT ' + m + ' OR ' + ATTR + ':"' + loc + '")'; }).join(' AND ')
      : UNRESTRICTED;
    if (!allowedList || (options && options.ignoreItemRestriction)) return scope;

    var restricted = scope + ' AND (' +
      allowedList.map(function (id) { return 'id=' + id; }).join(' OR ') + ')';
    if (restricted.length > MAX_FILTER_LENGTH) {
      if (!warnedTooLong && window.console) {
        warnedTooLong = true;
        window.console.warn('[b2b-catalog-scope] My Items has ' + allowedList.length +
          ' products, too many to filter search through the proxy; showing the full catalogue. The cart still enforces the restriction.');
      }
      return scope;
    }
    return restricted;
  }

  /* Whether the buyer may order a product. Accepts a numeric id, a numeric
   * string or a Shopify GID. Unrestricted buyers may order anything, and so
   * may anyone when the id is missing — the cart is the backstop for those. */
  function jendcoB2BItemAllowed(productId) {
    if (!allowedIds || productId == null || productId === '') return true;
    return allowedIds[String(productId).split('/').pop()] === true;
  }

  /* True when this buyer's Algolia filter really is limited to their My Items —
   * restricted, and the list within MAX_FILTER_LENGTH. The server-rendered
   * collection grid can only hide products page by page, so instant_search.js
   * hands such a buyer's grid to Algolia from the start. */
  function jendcoB2BSearchNarrowed() {
    return jendcoB2BFilter() !== jendcoB2BFilter({ ignoreItemRestriction: true });
  }

  window.jendcoB2BFilter = jendcoB2BFilter;
  window.jendcoB2BItemAllowed = jendcoB2BItemAllowed;
  window.jendcoB2BSearchNarrowed = jendcoB2BSearchNarrowed;
  window.jendcoB2BFilterParts = { attribute: ATTR, marker: MARKER, tag: TAG, unrestricted: UNRESTRICTED };
})();
